import {
  LuActivity,
  LuChartColumn,
  LuGift,
  LuHandCoins,
  LuLandmark,
  LuLayoutGrid,
  LuMessageSquare,
  LuPackage,
  LuReceipt,
  LuSettings,
  LuShoppingBasket,
  LuStore,
  LuTicket,
  LuShoppingCart,
  LuUsers,
  LuWallet,
} from "react-icons/lu";
import { ORDER_STAGES } from "../../pages/Order/orderStages";
import { ORDER_STATUS_VIEWS } from "../../pages/Order/orderStatuses";
import { REPORTS } from "../../pages/Reports/reportsConfig";
import { orderSectionKey, pageKey, reportKey } from "../../utils/permissions";
import { RESOURCES } from "../../pages/Resource/resources";

// Single source of truth for the sidebar and the router (see App.js).
//
//   leaf  : { path, name, icon, show, section? }
//   group : { name, icon, show, section?, children: [{ path, name, paths?, show? }] }  -> dropdown
//   section: heading the entry is listed under (see inSection below)
//
// `show` toggles a module per-deployment via REACT_APP_* env flags (anything
// except the literal string "false" is on). `paths` lists every route that
// should highlight the child (list + create/edit pages).
// Leaves/children that no page claims render the ComingSoon placeholder
// (see App.js).
const flag = (name) => process.env[name] !== "false";

// Child entry for a config-driven resource (pages/Resource/resources.js)
const resourceLink = (slug, show = true) => {
  const { title, listPath, formPath } = RESOURCES[slug];
  return {
    name: title,
    path: listPath,
    paths: [listPath, formPath].filter(Boolean),
    show,
    // the permission that opens the page (a role without it does not see the menu entry)
    perm: pageKey(slug, "view"),
  };
};

const link = (name, path, show = true, perm) => ({ name, path, paths: [path], show, perm });

// Sidebar sections: the entries of a section sit together under a small heading (the sidebar draws the heading
// where the section changes). Order = how often the team uses them: selling, catalog / promotions, suppliers, money,
// reports, then the system settings.
const inSection = (section, items) => items.map((item) => ({ ...item, section }));

export const MENU_ITEMS = [
  {
    path: "/dashboard/admin",
    name: "Dashboard",
    icon: LuLayoutGrid,
    show: true,
    perm: "dashboard.view",
  },

  /* ---------------------------------- Sales ---------------------------------- */
  ...inSection("Sales", [
    {
      name: "Order",
      icon: LuShoppingCart,
      show: flag("REACT_APP_ORDER"),
      // one list per status (pages/Order/orderStatuses.js) + tm's Remote Sale tabs as pages
      // (Purchasing, Tracking, Sorting, Delivery, Invoice, Refunded: pages/Order/orderStages.js);
      // order details (/dashboard/order/:id) highlight "All Orders"
      children: [
        // the first one (All Orders) carries the count of orders the user has not read yet
        // every order list carries the unread count of its own statuses (All Orders: all of them)
        ...ORDER_STATUS_VIEWS.map(({ title, path, status, slug }, index) => ({
          ...link(title, path, true, orderSectionKey(slug)),
          badge: index === 0 ? "unreadOrders" : { statuses: status.split(",") },
        })),
        ...ORDER_STAGES.map(({ title, path, slug, statuses, invoicePrint }) => ({
          ...link(title, path, flag(`REACT_APP_${slug.toUpperCase()}`), orderSectionKey(slug)),
          // the bulk invoice print page belongs to the Invoice page
          ...(invoicePrint && { paths: [path, "/dashboard/invoice-print"] }),
          badge: { statuses },
        })),
        // every generated invoice: find it by its invoice no, see it, print it again
        link("Invoice History", "/dashboard/invoice-history", true, "order.invoice"),
        // every refund (whole order or single items) with its items
        resourceLink("refund"),
      ],
    },
    // Customers (B2C): list, profile, wallet actions and ledger
    { ...resourceLink("customer", flag("REACT_APP_CUSTOMER")), icon: LuUsers },
    // What customers currently have in their cart
    { ...resourceLink("customer-cart", flag("REACT_APP_CUSTOMER_CART")), icon: LuShoppingBasket },
  ]),

  /* ----------------------------- Catalog & Promotion ----------------------------- */
  ...inSection("Catalog & Promotion", [
    // Product pricing of the web shop
    {
      name: "Product",
      icon: LuPackage,
      show: flag("REACT_APP_PRODUCT_MARKUP") || flag("REACT_APP_PRODUCT_DISCOUNT"),
      children: [
        // added to the supplier prices (by API provider / user type / level)
        { ...resourceLink("product-markup", flag("REACT_APP_PRODUCT_MARKUP")), name: "Markup" },
        // taken off the prices (all products or chosen categories)
        { ...resourceLink("product-discount", flag("REACT_APP_PRODUCT_DISCOUNT")), name: "Discount" },
      ],
    },
    // Coupons (applied at checkout: conditions by customer level / customer / API / amount / date)
    { ...resourceLink("coupon", flag("REACT_APP_COUPON")), icon: LuTicket },
    // Reward points: earning rules, points -> cash rates, customer balances and the report
    {
      name: "Reward",
      icon: LuGift,
      show: flag("REACT_APP_REWARD"),
      children: ["reward-rule", "reward-conversion", "reward-wallet", "reward-transaction"].map((slug) => resourceLink(slug)),
    },
  ]),

  /* --------------------------------- Suppliers --------------------------------- */
  // Vendors (suppliers settled in their own currency); wallet + ledger actions live on the list
  ...inSection("Suppliers", [{ ...resourceLink("vendor", flag("REACT_APP_VENDOR")), icon: LuStore }]),

  /* ---------------------------------- Finance ---------------------------------- */
  ...inSection("Finance", [
    {
      name: "Finance",
      icon: LuWallet,
      show: flag("REACT_APP_FINANCE"),
      children: [
        resourceLink("cash-receipt"),
        resourceLink("deposit-request"),
        resourceLink("credit-request"),
        resourceLink("asset"),
      ],
    },
    {
      name: "Accounts",
      icon: LuLandmark,
      show: flag("REACT_APP_ACCOUNTS"),
      children: [
        resourceLink("bank-account"),
        resourceLink("cheque-history", flag("REACT_APP_CHEQUE_HISTORY")),
        resourceLink("online-transaction"),
        resourceLink("bank-general-ledger"),
        resourceLink("deposit-gateway-fee"),
        resourceLink("agent-general-ledger"),
      ],
    },
    {
      name: "Voucher",
      icon: LuReceipt,
      show: flag("REACT_APP_VOUCHER"),
      children: [
        resourceLink("ledger-party"),
        resourceLink("receipt-head"),
        resourceLink("payment-head"),
        resourceLink("receipt-voucher"),
        resourceLink("payment-voucher"),
        resourceLink("voucher-ledger"),
        resourceLink("offline-service-transaction"),
      ],
    },
    {
      name: "Loan",
      icon: LuHandCoins,
      show: flag("REACT_APP_LOAN"),
      children: [
        resourceLink("loan-party"),
        resourceLink("loan"),
        resourceLink("loan-transaction"),
        link("Loan Calculator", "/dashboard/loan-calculator", true, "loan-calculator.view"),
      ],
    },
  ]),

  /* ---------------------------------- Insights ---------------------------------- */
  ...inSection("Insights", [
    {
      name: "Reports",
      icon: LuChartColumn,
      show: flag("REACT_APP_REPORTS"),
      // business reports of the web shop (pages/Reports): money, customers, suppliers, operations
      children: REPORTS.map((report) => link(report.title, report.path, true, reportKey(report.slug))),
    },
    {
      name: "Activity",
      icon: LuActivity,
      show: flag("REACT_APP_ACTIVITY"),
      children: [
        resourceLink("user-activity"),
        resourceLink("login-device"),
        resourceLink("login-activity"),
      ],
    },
  ]),

  /* ----------------------------------- System ----------------------------------- */
  ...inSection("System", [
    {
      name: "Employee",
      icon: LuUsers,
      show: flag("REACT_APP_EMPLOYEE"),
      children: [
        resourceLink("employee"),
        resourceLink("department"),
        resourceLink("designation"),
        resourceLink("role"),
        resourceLink("user-task"),
      ],
    },
    {
      name: "Communication",
      icon: LuMessageSquare,
      show: flag("REACT_APP_COMMUNICATION"),
      children: [
        resourceLink("sms-template"),
        resourceLink("email-template"),
        link("Send SMS", "/dashboard/send-sms", true, "send-sms.view"),
        link("Send Email", "/dashboard/send-email", true, "send-email.view"),
      ],
    },
    {
      name: "Setting",
      icon: LuSettings,
      show: flag("REACT_APP_SETTING"),
      children: [
        resourceLink("setting"),
        resourceLink("currency", flag("REACT_APP_CURRENCY")),
        resourceLink("currency-change-log", flag("REACT_APP_CURRENCY")),
        resourceLink("banner"),
        resourceLink("notice"),
      ],
    },
  ]),
];

// Visible items only: switched on by the env flags AND allowed for the role (`can` = usePermission().can). A group
// drops the children the role may not open and vanishes when none is left.
export const getVisibleMenu = (can = () => true) =>
  MENU_ITEMS.filter((item) => item.show && (item.children || can(item.perm)))
    .map((item) =>
      item.children
        ? { ...item, children: item.children.filter((child) => child.show !== false && can(child.perm)) }
        : item
    )
    .filter((item) => !item.children || item.children.length > 0);

// Every leaf ({ path, name }: plain items + group children), for route registration
export const getMenuLeaves = () =>
  MENU_ITEMS.flatMap((item) => (item.children ? item.children : [item])).map(
    ({ path, name }) => ({ path, name })
  );
